import { useParams, Link } from 'react-router';
import {
  Loader2,
  ArrowLeft,
  MessageSquare,
  Plus,
  UserX,
  LayoutGrid,
  ExternalLink,
  Calendar,
  Users as UsersIcon,
} from 'lucide-react';
import {
  useWhatsAppGroup,
  useAddGroupMember,
  useRemoveGroupMember,
} from '../hooks/useWhatsAppGroups';
import { Button, StatusChip } from '@lms/ui';
import { useState } from 'react';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';

export function WhatsAppGroupDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: group, isLoading, error } = useWhatsAppGroup(id!);
  const addMemberMutation = useAddGroupMember();
  const removeMemberMutation = useRemoveGroupMember();
  const { language, t } = useLanguage();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', phone: '' });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm font-body text-on-surface-variant">
          <Bilingual {...copy.whatsAppGroupDetail.loadingChannel} />
        </p>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="bg-error-container text-on-error-container p-6 rounded-xl">
        <h3 className="font-headline font-bold mb-2">
          <Bilingual {...copy.whatsAppGroupDetail.errorTitle} />
        </h3>
        <p className="text-sm font-body">
          {(error as Error)?.message || t(copy.whatsAppGroupDetail.errorFallback)}
        </p>
        <Link to="/whatsapp-groups" className="text-sm font-bold underline mt-4 block">
          <Bilingual {...copy.whatsAppGroupDetail.returnToChannels} />
        </Link>
      </div>
    );
  }

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    await addMemberMutation.mutateAsync({
      groupId: id!,
      data: newMember,
    });
    setIsAddModalOpen(false);
    setNewMember({ name: '', phone: '' });
  };

  const handleRemoveMember = async (phone: string) => {
    if (!window.confirm(t(copy.whatsAppGroupDetail.confirmRemove))) return;
    await removeMemberMutation.mutateAsync({
      groupId: id!,
      phone,
    });
  };

  return (
    <div className="space-y-10 animate-fadeIn">
      {/* Editorial Header */}
      <section className="flex flex-col gap-6">
        <Link
          to="/whatsapp-groups"
          className="flex items-center gap-2 text-xs font-label font-bold text-on-surface-variant uppercase tracking-widest hover:text-primary transition-colors no-underline group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          <Bilingual {...copy.whatsAppGroupDetail.backToChannels} />
        </Link>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <h1 className="text-4xl font-headline font-black text-on-surface tracking-tight m-0 uppercase leading-none">
                {group.name}
              </h1>
              <StatusChip
                label={group.gender}
                variant={
                  group.gender === 'IKHWAN'
                    ? 'success'
                    : group.gender === 'AKHWAT'
                      ? 'error'
                      : 'neutral'
                }
              />
            </div>
            <p className="text-lg font-body text-on-surface-variant max-w-3xl leading-relaxed">
              {group.description ||
                (language === 'ar'
                  ? copy.whatsAppGroupDetail.defaultDesc.ar
                  : copy.whatsAppGroupDetail.defaultDesc.id)}
            </p>
          </div>

          <div className="flex gap-4">
            {group.inviteLink && (
              <a
                href={group.inviteLink}
                target="_blank"
                rel="noreferrer"
                className="bg-surface-container-highest text-on-surface h-12 px-6 rounded-xl flex items-center gap-3 text-xs font-label font-bold uppercase tracking-widest shadow-level1 hover:bg-surface-container-high transition-all no-underline"
              >
                <ExternalLink size={16} />
                <Bilingual {...copy.whatsAppGroupDetail.whatsAppLink} />
              </a>
            )}
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="h-12 px-8 rounded-xl flex items-center gap-3 text-xs font-label font-bold uppercase tracking-widest"
            >
              <Plus size={18} />
              <Bilingual {...copy.whatsAppGroupDetail.btnAddMember} />
            </Button>
          </div>
        </div>
      </section>

      {/* Meta Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          label={t(copy.whatsAppGroupDetail.statTotalEntities)}
          val={`${group._count?.members || 0}`}
          sub={t(copy.whatsAppGroupDetail.statPopulation)}
          icon={UsersIcon}
        />
        <StatCard
          label={t(copy.whatsAppGroupDetail.statCapacity)}
          val={`${group.maxMembers}`}
          sub={t(copy.whatsAppGroupDetail.statMaxAllocation)}
          icon={LayoutGrid}
        />
        <StatCard
          label={t(copy.whatsAppGroupDetail.statSaturation)}
          val={`${Math.round(((group._count?.members || 0) / group.maxMembers) * 100)}%`}
          sub={t(copy.whatsAppGroupDetail.statChannelDensity)}
          icon={MessageSquare}
        />
        <StatCard
          label={t(copy.whatsAppGroupDetail.statAgeRange)}
          val={
            group.ageMin || group.ageMax
              ? `${group.ageMin ?? 0}-${group.ageMax ?? '∞'}`
              : t(copy.whatsAppGroupDetail.statOpen)
          }
          sub={t(copy.whatsAppGroupDetail.statDemographic)}
          icon={Calendar}
        />
      </div>

      {/* Member Matrix */}
      <div className="bg-surface-container-lowest rounded-3xl overflow-hidden shadow-level2 border border-on-surface/5">
        <div className="px-10 py-6 border-b border-on-surface/5 bg-surface-container-low/20">
          <h3 className="text-xs font-label font-black uppercase tracking-[0.3em] text-primary">
            <Bilingual {...copy.whatsAppGroupDetail.memberMatrix} />
          </h3>
        </div>

        <table className="w-full text-left">
          <thead>
            <tr className="bg-surface-container-low/50 border-b border-on-surface/5">
              <th className="px-10 py-5 text-[10px] font-label font-black text-on-surface-variant uppercase tracking-[0.2em]">
                <Bilingual {...copy.whatsAppGroupDetail.tableHeaderIdentity} />
              </th>
              <th className="px-10 py-5 text-[10px] font-label font-black text-on-surface-variant uppercase tracking-[0.2em]">
                <Bilingual {...copy.whatsAppGroupDetail.tableHeaderPhone} />
              </th>
              <th className="px-10 py-5 text-[10px] font-label font-black text-on-surface-variant uppercase tracking-[0.2em]">
                <Bilingual {...copy.whatsAppGroupDetail.tableHeaderEngagement} />
              </th>
              <th className="px-10 py-5 text-[10px] font-label font-black text-on-surface-variant uppercase tracking-[0.2em]"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-on-surface/5">
            {group.members?.map(member => (
              <tr
                key={member.id}
                className="hover:bg-surface-container-low/50 transition-colors group"
              >
                <td className="px-10 py-6">
                  <div className="text-sm font-body font-bold text-on-surface">{member.name}</div>
                </td>
                <td className="px-10 py-6">
                  <div className="text-xs font-mono text-on-surface-variant font-bold tracking-tight">
                    {member.phone}
                  </div>
                </td>
                <td className="px-10 py-6">
                  <StatusChip
                    label={
                      member.leftAt
                        ? t(copy.whatsAppGroupDetail.statusTerminated)
                        : t(copy.whatsAppGroupDetail.statusActive)
                    }
                    variant={member.leftAt ? 'neutral' : 'success'}
                  />
                </td>
                <td className="px-10 py-6 text-right">
                  <button
                    onClick={() => handleRemoveMember(member.phone)}
                    disabled={removeMemberMutation.isPending}
                    className="p-2 text-on-surface-variant hover:text-error transition-colors disabled:opacity-50"
                  >
                    {removeMemberMutation.isPending &&
                    removeMemberMutation.variables?.phone === member.phone ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <UserX size={16} />
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {(!group.members || group.members.length === 0) && (
          <div className="p-24 text-center">
            <UsersIcon size={48} className="mx-auto text-on-surface-variant/20 mb-4" />
            <p className="text-on-surface-variant font-body italic">
              <Bilingual {...copy.whatsAppGroupDetail.emptyMembers} />
            </p>
          </div>
        )}
      </div>

      {/* Simplified Add Member Modal (Bespoke UI) */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-surface/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-surface-container-lowest w-full max-w-md rounded-3xl shadow-level4 border border-on-surface/5 p-10 space-y-8 animate-scaleIn">
            <div>
              <h2 className="text-2xl font-headline font-black text-primary tracking-tight uppercase leading-none m-0">
                <Bilingual {...copy.whatsAppGroupDetail.modalAddTitle} />
              </h2>
              <p className="text-sm font-body text-on-surface-variant mt-2">
                <Bilingual {...copy.whatsAppGroupDetail.modalAddDesc} />
              </p>
            </div>

            <form onSubmit={handleAddMember} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-label font-black uppercase tracking-widest text-on-surface-variant ml-1">
                  <Bilingual {...copy.whatsAppGroupDetail.labelName} />
                </label>
                <input
                  required
                  className="w-full bg-surface-container border-none rounded-xl px-5 py-4 font-body text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                  placeholder={t(copy.whatsAppGroupDetail.placeholderName)}
                  value={newMember.name}
                  onChange={e => setNewMember({ ...newMember, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-label font-black uppercase tracking-widest text-on-surface-variant ml-1">
                  <Bilingual {...copy.whatsAppGroupDetail.labelPhone} />
                </label>
                <input
                  required
                  className="w-full bg-surface-container border-none rounded-xl px-5 py-4 font-body text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all font-mono"
                  placeholder={t(copy.whatsAppGroupDetail.placeholderPhone)}
                  value={newMember.phone}
                  onChange={e => setNewMember({ ...newMember, phone: e.target.value })}
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsAddModalOpen(false)}
                  className="flex-1 h-12 rounded-xl text-xs font-label font-bold uppercase tracking-widest"
                >
                  <Bilingual {...copy.whatsAppGroupDetail.btnCancel} />
                </Button>
                <Button
                  type="submit"
                  isLoading={addMemberMutation.isPending}
                  className="flex-1 h-12 rounded-xl text-xs font-label font-bold uppercase tracking-widest shadow-level2"
                >
                  <Bilingual {...copy.whatsAppGroupDetail.btnConfirm} />
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  val,
  sub,
  icon: Icon,
}: {
  label: string;
  val: string;
  sub: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}) {
  return (
    <div className="bg-surface-container-lowest p-6 rounded-2xl border border-on-surface/5 shadow-level1 group hover:border-primary/20 transition-all">
      <div className="flex items-center gap-3 text-primary/40 group-hover:text-primary transition-colors mb-4">
        <Icon size={16} />
        <span className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-on-surface-variant">
          {label}
        </span>
      </div>
      <div className="text-3xl font-headline font-black text-on-surface tracking-tighter leading-none">
        {val}
      </div>
      <div className="text-[10px] font-body text-on-surface-variant mt-2 font-bold opacity-40 uppercase tracking-widest">
        {sub}
      </div>
    </div>
  );
}
